import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '../ui/sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { KeyRound, Copy, Dices } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const genPassword = () => {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  const arr = new Uint32Array(10);
  window.crypto.getRandomValues(arr);
  arr.forEach((n) => { out += chars[n % chars.length]; });
  return out + '!';
};

// mode: 'create' | 'reset'
export const StaffLoginDialog = ({ open, onOpenChange, mode, member, account, onDone }) => {
  const [email, setEmail] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [role, setRole] = useState('staff');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null); // { email, tempPassword }

  useEffect(() => {
    if (open) {
      setEmail(mode === 'reset' ? (account?.email || '') : (member?.email || ''));
      setTempPassword(genPassword());
      setRole('staff');
      setIsActive(true);
      setCreated(null);
    }
  }, [open, mode, member, account]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (mode === 'create') {
        await axios.post(`${API_URL}/api/accounts`, {
          member_id: member.id, email: email.trim(), temp_password: tempPassword,
          role, is_active: isActive,
        });
        toast.success(`Login created for ${member.name}`);
      } else {
        await axios.post(`${API_URL}/api/accounts/${member.id}/reset-password`, {
          temp_password: tempPassword,
        });
        toast.success(`Password reset for ${member.name}`);
      }
      setCreated({ email: mode === 'create' ? email.trim() : account?.email, tempPassword });
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const copyCreds = () => {
    navigator.clipboard.writeText(`CareDesk login\nEmail: ${created.email}\nTemporary password: ${created.tempPassword}\nYou will be asked to set a new password at first login.`);
    toast.success('Setup instructions copied to clipboard');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="staff-login-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            {mode === 'create' ? 'Create Staff Login' : 'Reset Password'}
          </DialogTitle>
          <DialogDescription>
            {member?.name} · The employee must change this temporary password at first login.
          </DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="space-y-3 py-2" data-testid="login-credentials-panel">
            <div className="rounded-lg border border-[#1D9E75]/50 bg-[#D7F2E7]/50 p-4 space-y-1.5">
              <div className="text-sm"><span className="text-muted-foreground">Login email:</span> <span className="font-semibold" data-testid="created-email">{created.email}</span></div>
              <div className="text-sm"><span className="text-muted-foreground">Temporary password:</span> <span className="font-mono font-semibold" data-testid="created-password">{created.tempPassword}</span></div>
            </div>
            <p className="text-xs text-muted-foreground">
              Share these with {member?.name} in person or by phone. Email/SMS delivery is coming in a later phase — you can reopen this from the Team page any time by resetting the password.
            </p>
            <Button variant="outline" className="w-full" onClick={copyCreds} data-testid="copy-credentials-button">
              <Copy className="h-4 w-4 mr-2" /> Copy Setup Instructions
            </Button>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            {mode === 'create' && (
              <>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Login email</label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="employee@myaligninc.com" data-testid="login-email-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Role</label>
                    <Select value={role} onValueChange={setRole}>
                      <SelectTrigger className="bg-background" data-testid="login-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-background">
                        <SelectItem value="staff">RSS Staff</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Status</label>
                    <Select value={isActive ? 'active' : 'inactive'} onValueChange={(v) => setIsActive(v === 'active')}>
                      <SelectTrigger className="bg-background" data-testid="login-active-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-background">
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Temporary password</label>
              <div className="flex gap-2">
                <Input value={tempPassword} onChange={(e) => setTempPassword(e.target.value)} className="font-mono" data-testid="temp-password-input" />
                <Button type="button" variant="outline" size="icon" onClick={() => setTempPassword(genPassword())} title="Generate" data-testid="generate-password-button">
                  <Dices className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Minimum 8 characters. The employee will be forced to change it at first login.</p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="login-dialog-close">
            {created ? 'Done' : 'Cancel'}
          </Button>
          {!created && (
            <Button onClick={handleSave} disabled={saving || (mode === 'create' && !email.trim()) || tempPassword.length < 8} data-testid="login-dialog-save">
              {saving ? 'Saving…' : mode === 'create' ? 'Create Login' : 'Reset Password'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
